// src/pages/FirmPage.jsx
import React from 'react';
import { MapPin, Phone, Mail, Clock, Award, BookOpen, Scale } from 'lucide-react';
// Import the Timeline component (uncomment when needed)
// import Timeline from '../components/sections/Timeline';

const FirmPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <div className="bg-primary text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">About Our Firm</h1>
          <p className="text-xl max-w-3xl mx-auto">
            A boutique law firm with full-service litigation expertise and a client-centered approach
          </p>
        </div>
      </div>

      {/* Firm Overview Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-primary mb-6">Our Story</h2>
              <p className="text-gray-700 mb-4">
                AMV Law was founded with a vision to provide exceptional legal services that combine traditional expertise with modern, innovative approaches. Our firm has grown from a small practice to a respected name in the Indian legal landscape.
              </p>
              <p className="text-gray-700 mb-4">
                We pride ourselves on our ability to handle complex legal matters across various practice areas while maintaining a personalized approach for each client. Our team comprises talented advocates who bring diverse perspectives and specialized knowledge to every case.
              </p>
              <p className="text-gray-700 mb-6">
                At AMV Law, we believe in building long-term relationships with our clients based on trust, transparency, and consistent delivery of results. This philosophy has guided our practice since inception and continues to be the cornerstone of our firm's culture.
              </p>
            </div>
            
            <div className="rounded-lg overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1568992687947-868a62a9f521?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80" 
                alt="AMV Law Office Building" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary">Our Mission & Vision</h2>
            <div className="w-20 h-1 bg-secondary mx-auto my-4"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-6">
                <Scale className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-700">
                To deliver exceptional legal solutions that address our clients' unique challenges through a combination of legal expertise, industry knowledge, and innovative thinking. We are committed to upholding the highest ethical standards while advocating fiercely for our clients' interests.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-6">
                <Award className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-700">
                To be recognized as a leading boutique law firm that sets new standards for legal excellence in India. We aspire to be known for our innovative approach, integrity, and ability to achieve exceptional results for clients while contributing positively to the legal profession and society.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Firm Milestones - COMMENTED OUT */}
      {/* 
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary">Our Journey</h2>
            <div className="w-20 h-1 bg-secondary mx-auto my-4"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Key milestones in our firm's history
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline line */}{/*
              <div className="absolute left-6 h-full w-1 bg-primary-100"></div>
              
              {/* Timeline items */}{/*
              <div className="space-y-12">
                <div className="relative pl-20">
                  <div className="absolute left-0 flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white font-bold">
                    2012
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Firm Founding</h3>
                    <p className="text-gray-700">
                      AMV Law was founded in New Delhi with a focus on criminal and civil litigation services.
                    </p>
                  </div>
                </div>
                
                <div className="relative pl-20">
                  <div className="absolute left-0 flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white font-bold">
                    2015
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Expansion of Practice Areas</h3>
                    <p className="text-gray-700">
                      The firm expanded its services to include corporate law, arbitration, and family law practice areas.
                    </p>
                  </div>
                </div>
                
                <div className="relative pl-20">
                  <div className="absolute left-0 flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white font-bold">
                    2018
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Noida Office Opening</h3>
                    <p className="text-gray-700">
                      We opened our second office in Noida to better serve clients in the NCR region and expand our reach.
                    </p>
                  </div>
                </div>
                
                <div className="relative pl-20">
                  <div className="absolute left-0 flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white font-bold">
                    2020
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Launch of Pro Bono Initiative</h3>
                    <p className="text-gray-700">
                      We formalized our commitment to social responsibility by launching a structured pro bono program.
                    </p>
                  </div>
                </div>
                
                <div className="relative pl-20">
                  <div className="absolute left-0 flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white font-bold">
                    2023
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Integration of AI Legal Solutions</h3>
                    <p className="text-gray-700">
                      Pioneered the adoption of AI-powered legal technology to enhance our service offerings and deliver more efficient solutions to our clients.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      */}
      
      {/* Our Approach Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary">Our Approach</h2>
            <div className="w-20 h-1 bg-secondary mx-auto my-4"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-6">
                <BookOpen className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Knowledge-Driven</h3>
              <p className="text-gray-700">
                We continuously invest in developing our legal knowledge and expertise, staying abreast of legal developments, emerging trends, and innovative strategies that benefit our clients.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-6">
                <Award className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Client-Centric</h3>
              <p className="text-gray-700">
                Our practice is built around understanding our clients' needs and objectives. We take the time to listen, advise, and develop strategies that align with their goals and expectations.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-6">
                <Clock className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Results-Oriented</h3>
              <p className="text-gray-700">
                We measure our success by the outcomes we achieve for our clients. Our approach focuses on delivering practical, effective solutions that address immediate concerns while considering long-term implications.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary">Our Offices</h2>
            <div className="w-20 h-1 bg-secondary mx-auto my-4"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Delhi Office</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <MapPin className="h-5 w-5 text-primary mt-1 mr-3" />
                  <p className="text-gray-700">4/24, 3rd Floor, East Patel Nagar, Delhi - 110007</p>
                </div>
                
                <div className="flex items-start">
                  <Phone className="h-5 w-5 text-primary mt-1 mr-3" />
                  <p className="text-gray-700">+91-011-41634948</p>
                </div>
                
                <div className="flex items-start">
                  <Clock className="h-5 w-5 text-primary mt-1 mr-3" />
                  <p className="text-gray-700">Monday to Friday: 9:30 AM - 6:30 PM</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Noida Office</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <MapPin className="h-5 w-5 text-primary mt-1 mr-3" />
                  <p className="text-gray-700">H-157, Sector 63 Road, Sector 62, Noida, UP - 201301</p>
                </div>
                
                <div className="flex items-start">
                  <Phone className="h-5 w-5 text-primary mt-1 mr-3" />
                  <p className="text-gray-700">+91-9990358009</p>
                </div>
                
                <div className="flex items-start">
                  <Clock className="h-5 w-5 text-primary mt-1 mr-3" />
                  <p className="text-gray-700">Monday to Friday: 9:30 AM - 6:30 PM</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-12 h-96 rounded-lg overflow-hidden shadow-lg">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3501.551158433352!2d77.16713231508338!3d28.6475773824054!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d0337ed0a3cbb%3A0xc62c9b1ea8f0d8fe!2sEast%20Patel%20Nagar%2C%20Patel%20Nagar%2C%20New%20Delhi%2C%20Delhi%20110008!5e0!3m2!1sen!2sin!4v1617546335617!5m2!1sen!2sin" 
              width="100%" 
              height="100%" 
              style={{border:0}} 
              allowFullScreen="" 
              loading="lazy"
              title="AMV Law Office Location"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FirmPage;